import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Brush } from 'recharts';
import { format, parseISO, isValid } from 'date-fns';

interface InventoryTimeSeriesGraphProps {
  data: any[];
  selectedSkus?: string[];
}

// Color palette for up to 10 SKUs
const COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(142, 76%, 36%)',
  'hsl(280, 65%, 60%)',
  'hsl(45, 93%, 47%)',
  'hsl(340, 75%, 55%)',
  'hsl(200, 80%, 50%)',
];

const InventoryTimeSeriesGraph: React.FC<InventoryTimeSeriesGraphProps> = ({ data, selectedSkus = [] }) => {
  // Transform data from [{date, sku, quantity}] to [{date, sku1: qty, sku2: qty, ...}]
  const graphData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // When NO SKU filter is applied, aggregate total quantities by date
    if (selectedSkus.length === 0) {
      const dateMap = new Map<string, number>();

      data.forEach((row) => {
        const dateKey = row.date;
        const quantity = Number(row.quantity ?? row.Held ?? 0);

        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, 0);
        }

        dateMap.set(dateKey, dateMap.get(dateKey)! + quantity);
      });

      return Array.from(dateMap.entries())
        .map(([date, totalQuantity]) => ({ 
          date, 
          'Total Inventory': totalQuantity 
        }))
        .sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateA.getTime() - dateB.getTime();
        });
    }

    // When SKU filters ARE applied, show individual SKU lines (current behavior)
    const dateMap = new Map<string, any>();

    data.forEach((row) => {
      const dateKey = row.date;
      const sku = row.sku || row['SKU (Unique Identifier)'];
      const quantity = Number(row.quantity ?? row.Held ?? 0);

      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, { date: dateKey });
      }

      const dateEntry = dateMap.get(dateKey);
      dateEntry[sku] = quantity;
    });

    return Array.from(dateMap.values()).sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    });
  }, [data, selectedSkus]);

  // Get unique SKUs from data
  const skus = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // When no SKU filter, show "Total Inventory" line
    if (selectedSkus.length === 0) {
      return ['Total Inventory'];
    }
    
    return selectedSkus
  }, [data, selectedSkus]);

  const formatXAxis = (dateStr: string) => {
    try {
      const dateObj = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
      if (isValid(dateObj)) {
        return format(dateObj, 'MMM d');
      }
    } catch (e) {
      // fallback
    }
    return dateStr;
  };

  const formatTooltipLabel = (dateStr: string) => {
    try {
      const dateObj = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
      if (isValid(dateObj)) {
        return format(dateObj, 'MMM d, yyyy');
      }
    } catch (e) {
      // fallback
    }
    return dateStr;
  };

  if (graphData.length === 0) {
    return (
      <div className="flex items-center justify-center h-[400px] text-muted-foreground">
        No data available to display. Please select a date range to view the graph.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart data={graphData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
        <XAxis 
          dataKey="date" 
          tickFormatter={formatXAxis}
          stroke="hsl(var(--muted-foreground))"
          style={{ fontSize: '12px' }}
        />
        <YAxis 
          stroke="hsl(var(--muted-foreground))"
          style={{ fontSize: '12px' }}
        />
        <Tooltip
          labelFormatter={formatTooltipLabel}
          contentStyle={{
            backgroundColor: 'hsl(var(--popover))',
            border: '1px solid hsl(var(--border))',
            borderRadius: '6px',
            color: 'hsl(var(--popover-foreground))',
          }}
          formatter={(value: any) => [typeof value === 'number' ? value.toLocaleString() : value, '']}
        />
        <Legend 
          wrapperStyle={{ fontSize: '12px' }}
          formatter={(value) => {
            // Truncate long SKU names in legend
            if (value.length > 25) return value.substring(0, 25) + '...';
            return value;
          }}
        />
        {skus.map((sku, index) => (
          <Line
            key={sku}
            type="monotone"
            dataKey={sku}
            stroke={COLORS[index % COLORS.length]}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
            name={sku}
            connectNulls
          />
        ))}
        <Brush
          dataKey="date"
          height={30}
          stroke="hsl(var(--primary))"
          fill="hsl(var(--muted))"
          tickFormatter={formatXAxis}
          travellerWidth={10}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default InventoryTimeSeriesGraph;
